import { useEffect, useRef } from "react";

import { useShipmentRoute } from "hooks";

import { RouteTab } from "./RouteTab";

import type { SuggestedRouteDeleteContext } from "./components/SuggestedRoute";

interface RouteShipmentLike {
  info: {
    id: number;
    route: number;
    type: ShipmentType;
  };
}

interface Props {
  shipment: RouteShipmentLike | undefined;
  // Enables the shipper-only affordance to delete the suggested route.
  // Pass true only for shippers with write access (owner / admin / read-and-write
  // sharee); omit for carrier/supplier/read-only views.
  canDeleteSuggestedRoute?: boolean;
}

export function RouteTabContainer(props: Props) {
  const { shipment, canDeleteSuggestedRoute = false } = props;
  const shipmentRef = useRef<undefined | RouteShipmentLike>();
  const { route, error, loading, refetchRoute } = useShipmentRoute(
    shipment?.info.route,
  );

  useEffect(() => {
    /*
    This hook keeps track of the shipment ID between renders to ensure
    that the route is re-fetched when another shipment is selected.
    */
    if (shipment && shipmentRef.current?.info?.id !== shipment.info.id) {
      shipmentRef.current = shipment;
      refetchRoute();
    }
  }, [shipment, refetchRoute]);

  if (!shipment) {
    return null;
  }

  const suggestedRouteDeleteContext: SuggestedRouteDeleteContext | undefined =
    canDeleteSuggestedRoute
      ? {
          shipmentId: shipment.info.id,
          shipmentType: shipment.info.type,
          onDeleted: refetchRoute,
        }
      : undefined;

  return (
    <RouteTab
      error={error}
      route={route}
      loading={loading}
      displayRoutePointDates={shipment.info.type !== "QUOTES_ONLY"}
      suggestedRouteDeleteContext={suggestedRouteDeleteContext}
    />
  );
}
